import React, { useState, useEffect } from 'react';
import {
  Download,
  Shield,
  Copy,
  Check,
  Cpu,
  HardDrive,
  ChevronDown,
  Sparkles,
  Layers,
  History,
  Calendar,
  Tag,
  Monitor,
  CheckCircle2,
  FileCode,
  RefreshCw,
  Info,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../context/AuthContext';
import {
  triggerBrowserDownload,
  trackDirectDownload,
  PENDING_DOWNLOAD_KEY,
} from '../../../utils/downloadResume';

const DEFAULT_PREVIOUS_VERSIONS = [
  {
    version: '1.0.3',
    releaseDate: 'September 18, 2026',
    tag: 'v1.0.3',
    status: 'Maintenance',
    highlights: 'Visual schema-diff inspector, Snowflake and Databricks streaming connectors, AST performance boost.',
    downloads: [
      {
        os: 'windows',
        name: 'Windows (.exe / .zip)',
        format: '.exe / .zip',
        size: '142 MB',
        downloadUrl: '/candor-exe/CandorAI-Setup-v1.0.3.zip',
        sha256: '3a8c4f1d9e2b7a6c5d4e3f2a1b0c9d8e7f6a5b4c3d2e1f0a9b8c7d6e5f4a3b2c',
      },
      {
        os: 'macos',
        name: 'macOS (Apple Silicon & Intel)',
        format: '.dmg',
        size: '150 MB',
        downloadUrl: '/candor-exe/CandorAI-mac-v1.0.3.dmg',
        fallbackUrl: '/candor-exe/CandorAI-Setup-v1.0.3.zip',
        sha256: 'b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c3',
      },
      {
        os: 'linux',
        name: 'Linux (.deb)',
        format: '.deb',
        size: '137 MB',
        downloadUrl: '/candor-exe/candor-ai_1.0.3_amd64.deb',
        fallbackUrl: '/candor-exe/CandorAI-Setup-v1.0.3.zip',
        sha256: 'c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b3c4d5',
      },
    ],
  },
  {
    version: '1.0.2',
    releaseDate: 'August 22, 2026',
    tag: 'v1.0.2',
    status: 'Archived',
    highlights: 'Automated Oracle identity column translation, PostgreSQL temporary table conversion, improved memory management.',
    downloads: [
      {
        os: 'windows',
        name: 'Windows (.exe / .zip)',
        format: '.exe / .zip',
        size: '140 MB',
        downloadUrl: '/candor-exe/CandorAI-Setup-v1.0.2.zip',
        sha256: 'd5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6',
      },
      {
        os: 'macos',
        name: 'macOS (Universal)',
        format: '.dmg',
        size: '148 MB',
        downloadUrl: '/candor-exe/CandorAI-mac-v1.0.2.dmg',
        fallbackUrl: '/candor-exe/CandorAI-Setup-v1.0.2.zip',
        sha256: 'e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6f7',
      },
      {
        os: 'linux',
        name: 'Linux (.deb)',
        format: '.deb',
        size: '135 MB',
        downloadUrl: '/candor-exe/candor-ai_1.0.2_amd64.deb',
        fallbackUrl: '/candor-exe/CandorAI-Setup-v1.0.2.zip',
        sha256: 'f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8',
      },
    ],
  },
  {
    version: '1.0.1',
    releaseDate: 'July 15, 2026',
    tag: 'v1.0.1',
    status: 'Archived',
    highlights: 'Hotfix for SSL certificate chain validation in enterprise private clouds, MySQL 8.0 support.',
    downloads: [
      {
        os: 'windows',
        name: 'Windows (.exe / .zip)',
        format: '.exe / .zip',
        size: '138 MB',
        downloadUrl: '/candor-exe/CandorAI-Setup-v1.0.1.zip',
        sha256: '0a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b',
      },
      {
        os: 'macos',
        name: 'macOS (Universal)',
        format: '.dmg',
        size: '146 MB',
        downloadUrl: '/candor-exe/CandorAI-mac-v1.0.1.dmg',
        fallbackUrl: '/candor-exe/CandorAI-Setup-v1.0.1.zip',
        sha256: '1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c',
      },
      {
        os: 'linux',
        name: 'Linux (.deb)',
        format: '.deb',
        size: '133 MB',
        downloadUrl: '/candor-exe/candor-ai_1.0.1_amd64.deb',
        fallbackUrl: '/candor-exe/CandorAI-Setup-v1.0.1.zip',
        sha256: '2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c3d',
      },
    ],
  },
  {
    version: '1.0.0',
    releaseDate: 'June 10, 2026',
    tag: 'v1.0.0',
    status: 'Initial GA',
    highlights: 'Initial General Availability release of Candor SI IDE with 1-shot T-SQL to Oracle schema transformation engine.',
    downloads: [
      {
        os: 'windows',
        name: 'Windows (.exe / .zip)',
        format: '.exe / .zip',
        size: '135 MB',
        downloadUrl: '/candor-exe/CandorAI-Setup-v1.0.0.zip',
        sha256: '3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c3d4e',
      },
      {
        os: 'macos',
        name: 'macOS (Universal)',
        format: '.dmg',
        size: '144 MB',
        downloadUrl: '/candor-exe/CandorAI-mac-v1.0.0.dmg',
        fallbackUrl: '/candor-exe/CandorAI-Setup-v1.0.0.zip',
        sha256: '4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c3d4e5f',
      },
      {
        os: 'linux',
        name: 'Linux (.deb)',
        format: '.deb',
        size: '130 MB',
        downloadUrl: '/candor-exe/candor-ai_1.0.0_amd64.deb',
        fallbackUrl: '/candor-exe/CandorAI-Setup-v1.0.0.zip',
        sha256: '5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c3d4e5f6a',
      },
    ],
  },
];

// Synchronous multi-heuristic OS & architecture detection
function detectUserEnvironmentSync() {
  if (typeof window === 'undefined') {
    return {
      os: 'windows',
      arch: 'x64',
      name: 'Windows',
      detail: 'Windows (64-bit)',
      browser: 'Browser',
      isMobile: false,
      isAutoDetected: false,
    };
  }

  const nav = window.navigator || {};
  const ua = nav.userAgent || '';
  const platform = nav.platform || '';
  const uadPlatform = nav.userAgentData?.platform || '';

  // Detect browser name for informative UI
  let browser = 'Browser';
  if (/Edg\//i.test(ua)) browser = 'Microsoft Edge';
  else if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) browser = 'Google Chrome';
  else if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) browser = 'Apple Safari';
  else if (/Firefox\//i.test(ua)) browser = 'Mozilla Firefox';
  else if (/Brave/i.test(ua)) browser = 'Brave';

  // 1. Mobile Check (iOS / Android)
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua) || nav.userAgentData?.mobile;
  if (isMobile) {
    if (/iPhone|iPad|iPod/i.test(ua)) {
      return {
        os: 'macos',
        arch: 'ARM64',
        name: 'iOS / iPadOS',
        detail: 'Apple Mobile Device',
        browser,
        isMobile: true,
        isAutoDetected: true,
      };
    }
    return {
      os: 'windows',
      arch: 'x64',
      name: 'Android',
      detail: 'Android Mobile Device',
      browser,
      isMobile: true,
      isAutoDetected: true,
    };
  }

  // 2. macOS Detection
  if (
    /Macintosh|MacIntel|MacPPC|Mac68K/i.test(platform) ||
    /Mac OS X/i.test(ua) ||
    /macOS/i.test(uadPlatform)
  ) {
    let isAppleSilicon = false;
    // Check WebGL GPU renderer for Apple Silicon (M1/M2/M3/M4 or Apple GPU)
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (gl) {
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
          const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '';
          if (/Apple/i.test(renderer) && !/Intel/i.test(renderer)) {
            isAppleSilicon = true;
          }
        }
      }
    } catch (_) {}

    // Fallback Apple Silicon check
    if (!isAppleSilicon && (nav.maxTouchPoints > 0 || /ARM/i.test(ua))) {
      isAppleSilicon = true;
    }

    return {
      os: 'macos',
      arch: isAppleSilicon ? 'ARM64' : 'x64',
      name: 'macOS',
      detail: isAppleSilicon ? 'macOS (Apple Silicon M-Series)' : 'macOS (Intel 64-bit)',
      browser,
      isMobile: false,
      isAutoDetected: true,
    };
  }

  // 3. Linux Detection
  if (/Linux/i.test(platform) || /Linux/i.test(ua) || /Linux/i.test(uadPlatform)) {
    let linuxDetail = 'Linux (x86_64)';
    if (/Ubuntu/i.test(ua)) linuxDetail = 'Ubuntu Linux (64-bit)';
    else if (/Debian/i.test(ua)) linuxDetail = 'Debian Linux (64-bit)';
    else if (/Fedora/i.test(ua)) linuxDetail = 'Fedora Linux (64-bit)';
    else if (/Arch/i.test(ua)) linuxDetail = 'Arch Linux (64-bit)';

    return {
      os: 'linux',
      arch: 'x64',
      name: 'Linux',
      detail: linuxDetail,
      browser,
      isMobile: false,
      isAutoDetected: true,
    };
  }

  // 4. Windows Detection (Default)
  const isArm = /ARM64|ARM/i.test(ua);
  const isWin11or10 = /Windows NT 10.0/i.test(ua);
  const detail = isArm
    ? 'Windows 11 on ARM'
    : isWin11or10
    ? 'Windows 10 / 11 (64-bit)'
    : 'Windows (64-bit)';

  return {
    os: 'windows',
    arch: isArm ? 'ARM64' : 'x64',
    name: 'Windows',
    detail,
    browser,
    isMobile: false,
    isAutoDetected: true,
  };
}

// Brand SVG Icons
function WindowsIcon({ className = 'w-6 h-6' }) {
  return (
    <svg className={className} viewBox="0 0 88 88" fill="currentColor">
      <path d="M0 12.5L35.7 7.6V41.7H0V12.5ZM0 46.3H35.7V80.4L0 75.5V46.3ZM40.1 7.1L87.8 0V41.7H40.1V7.1ZM40.1 46.3H87.8V88L40.1 80.9V46.3Z" />
    </svg>
  );
}

function AppleIcon({ className = 'w-6 h-6' }) {
  return (
    <svg className={className} viewBox="0 0 170 170" fill="currentColor">
      <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.35.13-9.16-1.9-14.42-6.08-3.7-3.07-7.73-7.94-12.1-14.61-6.19-9.5-11.05-20.3-14.58-32.39-3.53-12.1-5.3-23.73-5.3-34.9 0-14.07 3.52-25.95 10.57-35.63 7.05-9.69 16.03-14.62 26.94-14.8 5.17 0 10.98 1.48 17.43 4.43 6.45 2.96 10.74 4.49 12.87 4.59 1.74 0 6.26-1.63 13.57-4.88 7.3-3.26 13.43-4.63 18.38-4.12 14.05 1.07 24.89 6.27 32.53 15.61-12.61 7.64-18.75 18.06-18.42 31.27.32 10.42 4.26 19.16 11.82 26.23 7.56 7.06 16.59 11.25 27.09 12.56-2.5 7.62-5.65 15.22-9.45 22.81zM119.22 33.15c0-7.39 2.65-14.54 7.94-21.46 5.3-6.92 11.85-11.23 19.66-12.94 1.09 7.72-1.09 15.02-6.53 21.91-5.44 6.89-12.21 11.19-20.32 12.89-.11-.13-.75-.4-.75-.4z" />
    </svg>
  );
}

function LinuxIcon({ className = 'w-6 h-6' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2a4 4 0 0 0-4 4v3.5a1.5 1.5 0 0 0 .5 1.14c.32.28.5.69.5 1.11 0 1.93-1.57 3.5-3.5 3.5H5a2 2 0 0 0-2 2c0 .55.45 1 1 1h.5a1.5 1.5 0 0 0 1.5-1.5c0-.28.22-.5.5-.5h.5c.28 0 .5.22.5.5A2.5 2.5 0 0 0 9 19h6a2.5 2.5 0 0 0 2-1.5c0-.28.22-.5.5-.5h.5c.28 0 .5.22.5.5A1.5 1.5 0 0 0 19.5 19H20a1 1 0 0 0 1-1 2 2 0 0 0-2-2h-.5c-1.93 0-3.5-1.57-3.5-3.5 0-.42.18-.83.5-1.11A1.5 1.5 0 0 0 16 9.5V6a4 4 0 0 0-4-4zm-2 5a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm4 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm-3 2h2a1 1 0 0 1 1 1c0 .55-.45 1-1 1h-2a1 1 0 0 1-1-1c0-.55.45-1 1-1z" />
    </svg>
  );
}

export default function DownloadPage({ onOpenDownload, onOpenDownloadModal, onOpenContact, onOpenLogin }) {
  const { isAuthenticated, user } = useAuth();
  const [detectedEnv, setDetectedEnv] = useState(() => detectUserEnvironmentSync());
  const [selectedHeroOs, setSelectedHeroOs] = useState(null); // null = use detectedEnv.os
  const [selectedHeroBuildId, setSelectedHeroBuildId] = useState(null); // null = use recommended build
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'windows' | 'macos' | 'linux'
  const [manifestData, setManifestData] = useState(null);
  const [copiedSha, setCopiedSha] = useState('');
  const [selectedShaModal, setSelectedShaModal] = useState(null);
  const [activeInstallTab, setActiveInstallTab] = useState('windows');
  const [expandedFaq, setExpandedFaq] = useState(0);
  const [selectedArchiveVersion, setSelectedArchiveVersion] = useState('all');

  useEffect(() => {
    // 1. Initial synchronous detection
    const syncEnv = detectUserEnvironmentSync();
    setDetectedEnv(syncEnv);

    // 2. High-entropy Client Hints for Chromium browsers (Chrome, Edge, Brave, etc.)
    if (typeof window !== 'undefined' && window.navigator?.userAgentData?.getHighEntropyValues) {
      window.navigator.userAgentData
        .getHighEntropyValues(['architecture', 'bitness', 'platform', 'platformVersion', 'model'])
        .then((hints) => {
          const rawPlatform = (hints.platform || window.navigator.userAgentData.platform || '').toLowerCase();
          const isArm = hints.architecture === 'arm';
          const arch = isArm ? 'ARM64' : (hints.bitness === '32' ? 'x86' : 'x64');
          let os = syncEnv.os;
          let name = syncEnv.name;
          let detail = syncEnv.detail;

          if (rawPlatform.includes('win')) {
            os = 'windows';
            name = 'Windows';
            const majorVersion = parseInt(hints.platformVersion?.split('.')[0] || '0', 10);
            const winName = majorVersion >= 13 ? 'Windows 11' : 'Windows 10';
            detail = isArm ? `${winName} on ARM` : `${winName} (${arch})`;
          } else if (rawPlatform.includes('mac')) {
            os = 'macos';
            name = 'macOS';
            detail = isArm ? 'macOS (Apple Silicon M-Series)' : 'macOS (Intel 64-bit)';
          } else if (rawPlatform.includes('lin')) {
            os = 'linux';
            name = 'Linux';
            detail = `Linux (${arch})`;
          }

          setDetectedEnv((prev) => ({
            ...prev,
            os,
            name,
            arch,
            detail,
            isAutoDetected: true,
            highEntropyVerified: true,
          }));
        })
        .catch(() => {
          // Ignore error, keep sync detection
        });
    }
  }, []);

  useEffect(() => {
    if (window.location.hash === '#previous-releases') {
      setIsArchiveModalOpen(true);
      const el = document.getElementById('previous-releases');
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 150);
      }
    }
  }, []);

  useEffect(() => {
    fetch('/update_manifest.json')
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error('Manifest not found');
      })
      .then((data) => {
        if (data) setManifestData(data);
      })
      .catch((err) => {
        console.warn('Could not load /update_manifest.json:', err);
      });
  }, []);

  // Platforms data fallback if manifest is loading or unavailable
  const version = manifestData?.version || '1.0.4';
  const releaseDate = manifestData?.releaseDate || 'October 2026';

  const platforms = manifestData?.platforms || {
    windows: {
      name: 'Windows',
      os: 'windows',
      version,
      tagline: 'Windows 10, 11 (64-bit)',
      architectures: [
        {
          id: 'win-exe',
          name: 'Windows Installer (.exe)',
          arch: 'x64',
          format: '.exe',
          downloadUrl: `/candor-exe/CandorAI-Setup-v${version}.exe`,
          fallbackUrl: manifestData?.initial_download_link || `/candor-exe/CandorAI-Setup-v${version}.zip`,
          size: '~145 MB',
          sha256: '8f4a2b1c9e3d7a6b5c4d3e2f1a0b9c8d7e6f5a4b3c2d1e0f9a8b7c6d5e4f3a2b',
          badge: 'Recommended (.exe)',
          status: 'available',
        },
        {
          id: 'win-zip',
          name: 'Portable Archive (.zip)',
          arch: 'x64',
          format: '.zip',
          downloadUrl: manifestData?.initial_download_link || `/candor-exe/CandorAI-Setup-v${version}.zip`,
          size: '~140 MB',
          sha256: '4b3c2d1e0f9a8b7c6d5e4f3a2b1c9e3d7a6b5c4d3e2f1a0b9c8d7e6f5a4b3c2d',
          badge: 'Portable ZIP (.zip)',
          status: 'available',
        },
      ],
    },
    macos: {
      name: 'macOS',
      os: 'macos',
      version,
      tagline: 'macOS 12.0 Monterey or later',
      architectures: [
        {
          id: 'mac-arm64',
          name: 'Apple Silicon',
          arch: 'ARM64 (M1/M2/M3/M4)',
          format: '.dmg',
          downloadUrl: `/candor-exe/CandorAI-mac-arm64-v${version}.dmg`,
          fallbackUrl: manifestData?.initial_download_link || `/candor-exe/CandorAI-Setup-v${version}.zip`,
          size: '~152 MB',
          sha256: 'a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2',
          badge: 'Apple Silicon',
          status: 'available',
        },
        {
          id: 'mac-intel',
          name: 'Intel Mac',
          arch: 'x64 (Intel)',
          format: '.dmg',
          downloadUrl: `/candor-exe/CandorAI-mac-x64-v${version}.dmg`,
          fallbackUrl: manifestData?.initial_download_link || `/candor-exe/CandorAI-Setup-v${version}.zip`,
          size: '~155 MB',
          sha256: 'c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2a1b2',
          badge: 'Intel x64',
          status: 'available',
        },
      ],
    },
    linux: {
      name: 'Linux',
      os: 'linux',
      version,
      tagline: 'Debian, Ubuntu & Linux Mint (.deb 64-bit)',
      architectures: [
        {
          id: 'linux-deb',
          name: 'Debian / Ubuntu Package',
          arch: 'x64',
          format: '.deb',
          downloadUrl: `/candor-exe/candor-ai_${version}_amd64.deb`,
          fallbackUrl: manifestData?.initial_download_link || `/candor-exe/CandorAI-Setup-v${version}.zip`,
          size: '~138 MB',
          sha256: 'd4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c3d4e5',
          badge: '.deb Package',
          status: 'available',
        },
      ],
    },
  };

  // Selected OS for the hero recommendation card (either manually overridden or auto-detected)
  const currentHeroOsKey = selectedHeroOs || detectedEnv.os || 'windows';
  const heroPlatformData = platforms[currentHeroOsKey] || platforms.windows;
  const isUsingDetectedOs = !selectedHeroOs || selectedHeroOs === detectedEnv.os;

  // Find the optimal/default build for this OS based on detected architecture
  const defaultBuildForHeroOs =
    heroPlatformData.architectures.find((a) => {
      if (detectedEnv.os === currentHeroOsKey) {
        if (currentHeroOsKey === 'windows') return a.format === '.exe' || a.id.includes('exe');
        if (currentHeroOsKey === 'macos') {
          return detectedEnv.arch === 'ARM64' ? a.id.includes('arm64') : a.id.includes('intel');
        }
        if (currentHeroOsKey === 'linux') return a.format === '.deb' || a.id.includes('deb');
      }
      return a.badge?.includes('Recommended') || a.id.includes('exe') || a.id.includes('arm64') || a.id.includes('deb');
    }) || heroPlatformData.architectures[0];

  // Active build in hero card (if user picked a specific package format chip, use it)
  const activeHeroBuild =
    (selectedHeroBuildId &&
      heroPlatformData.architectures.find((a) => a.id === selectedHeroBuildId)) ||
    defaultBuildForHeroOs;

  // Clean filename for the suggested file
  const suggestedFileName = (() => {
    const rawUrl = activeHeroBuild.downloadUrl || activeHeroBuild.fallbackUrl || '';
    const parts = rawUrl.split('/');
    return parts[parts.length - 1] || `CandorAI-${currentHeroOsKey}-v${version}.zip`;
  })();

  const previousVersionsList = manifestData?.previous_versions || DEFAULT_PREVIOUS_VERSIONS;
  const filteredPreviousVersions =
    selectedArchiveVersion === 'all'
      ? previousVersionsList
      : previousVersionsList.filter((v) => v.version === selectedArchiveVersion);

  const handleDownloadItem = (item, platformName) => {
    const targetUrl = item.downloadUrl || item.fallbackUrl;

    if (isAuthenticated) {
      triggerBrowserDownload(targetUrl);
      toast.success(`Starting download: ${item.name} for ${platformName}!`);
      trackDirectDownload(user, targetUrl, `download_page_${item.id || item.os || 'binary'}`);
    } else {
      // Stamp download intent with target OS build
      try {
        sessionStorage.setItem(
          PENDING_DOWNLOAD_KEY,
          JSON.stringify({
            url: targetUrl,
            platform: platformName,
            name: item.name,
            at: Date.now(),
            flow: 'download',
          })
        );
      } catch (err) {
        console.warn('SessionStorage error:', err);
      }
      if (onOpenDownloadModal) {
        onOpenDownloadModal(targetUrl);
      } else if (onOpenDownload) {
        onOpenDownload(targetUrl);
      }
    }
  };

  const handleCopySha = (hash) => {
    if (!hash) return;
    navigator.clipboard.writeText(hash).then(() => {
      setCopiedSha(hash);
      toast.success('SHA-256 checksum copied to clipboard!');
      setTimeout(() => setCopiedSha(''), 3000);
    });
  };

  const faqs = [
    {
      q: 'Is Candor SI IDE free to download and use?',
      a: 'Yes! The Community Edition of Candor SI IDE is free to download for individual data engineers, database administrators, and developers. Free accounts include access to local schema reverse engineering, 1-shot pipeline generation, and AI-assisted migrations.',
    },
    {
      q: 'How do automatic updates work?',
      a: 'Candor IDE features a built-in background update checker. When a new release or hotfix is published to our release channels, you will be notified in the IDE footer with a 1-click update prompt.',
    },
    {
      q: 'Can Candor IDE run in air-gapped or restricted network environments?',
      a: 'Yes. Candor operates local-first. Schema introspection, AST transformations, and visual pipeline diagrams run directly on your workstation without transmitting proprietary table definitions or database credentials to outside servers.',
    },
    {
      q: 'Do you offer an MSI or unattended installer for enterprise rollouts?',
      a: 'Yes. For automated enterprise deployments via Microsoft Intune, SCCM, Homebrew, or Jamf, please contact our enterprise team for customized MSI/PKG packages and volume licensing.',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans selection:bg-blue-500 selection:text-white">
      {/* ── Top Hero Section ── */}
      <section className="relative pt-12 pb-16 sm:pt-20 sm:pb-24 overflow-hidden border-b border-slate-200/80 bg-gradient-to-b from-white via-blue-50/20 to-slate-50">
        <div className="absolute inset-0 bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:16px_16px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_35%,#000_70%,transparent_100%)] opacity-70 pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Release Version Badge */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-200/80 text-blue-700 text-xs sm:text-sm font-semibold shadow-sm mb-6 sm:mb-8 animate-fade-in">
            <span className="flex h-2 w-2 rounded-full bg-blue-600 animate-pulse" />
            <span>Candor SI Desktop IDE • Multi-Platform Release v{version}</span>
            <span className="text-slate-400">|</span>
            <span className="text-slate-600 font-medium">{releaseDate}</span>
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight max-w-4xl mx-auto leading-tight sm:leading-[1.18]">
            <span className="block">Choose Your Operating System.</span>
            <span className="block mt-2.5 sm:mt-4 bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 bg-clip-text text-transparent pb-1">
              Vibe Coding for Data Platforms.
            </span>
          </h1>

          <p className="mt-6 sm:mt-8 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Native desktop experience for schema-aware data platform engineering, automated migrations, and zero-latency SQL/Python workflows on Windows, macOS, and Linux.
          </p>

          {/* ── Auto-Detected OS Recommendation Hero Card ── */}
          <div className="mt-8 sm:mt-12 max-w-3xl mx-auto bg-white rounded-3xl shadow-xl shadow-blue-900/5 border border-slate-200/90 p-6 sm:p-8 relative transition-all hover:shadow-2xl hover:shadow-blue-900/10 hover:border-blue-300">
            {/* Top Status & Detection Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 pb-5 mb-6 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>
                <span className="text-xs font-semibold text-slate-700">
                  {isUsingDetectedOs ? (
                    <>
                      System Detected:{' '}
                      <strong className="text-blue-700">{detectedEnv.detail}</strong>
                    </>
                  ) : (
                    <>
                      Selected Platform:{' '}
                      <strong className="text-blue-700">{heroPlatformData.name}</strong>
                    </>
                  )}
                </span>
                {detectedEnv.browser && detectedEnv.browser !== 'Browser' && (
                  <span className="hidden sm:inline-block text-[11px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 font-medium border border-slate-200/60">
                    via {detectedEnv.browser}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                {!isUsingDetectedOs ? (
                  <button
                    onClick={() => {
                      setSelectedHeroOs(null);
                      setSelectedHeroBuildId(null);
                    }}
                    className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1.5 hover:underline cursor-pointer"
                    title="Switch back to your auto-detected operating system"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Reset to Detected ({detectedEnv.name})
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    Recommended for your PC
                  </span>
                )}
              </div>
            </div>

            {/* Mobile Device Notice if browsing from phone/tablet */}
            {detectedEnv.isMobile && (
              <div className="mb-5 p-3.5 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-center gap-2.5 text-left">
                <Info className="w-4 h-4 text-amber-600 shrink-0" />
                <span>
                  <strong>Mobile device detected:</strong> Candor IDE is a native desktop application. You can download the desktop file below or email this link to your workstation.
                </span>
              </div>
            )}

            {/* Main Recommendation Row */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 text-left">
              {/* Left: Icon & Details */}
              <div className="flex items-start sm:items-center gap-4 sm:gap-5 min-w-0 flex-1">
                <div
                  className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center shrink-0 shadow-md ${
                    currentHeroOsKey === 'windows'
                      ? 'bg-blue-600 text-white shadow-blue-500/20'
                      : currentHeroOsKey === 'macos'
                      ? 'bg-slate-900 text-white shadow-slate-900/20'
                      : 'bg-amber-600 text-white shadow-amber-600/20'
                  }`}
                >
                  {currentHeroOsKey === 'windows' && <WindowsIcon className="w-10 h-10 sm:w-11 sm:h-11" />}
                  {currentHeroOsKey === 'macos' && <AppleIcon className="w-10 h-10 sm:w-11 sm:h-11" />}
                  {currentHeroOsKey === 'linux' && <LinuxIcon className="w-10 h-10 sm:w-11 sm:h-11" />}
                </div>

                <div className="min-w-0 space-y-1.5 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-bold text-blue-600 uppercase tracking-wider">
                      Candor SI Desktop IDE
                    </span>
                    <span className="text-slate-300">•</span>
                    <span className="text-xs text-slate-500 font-medium">
                      v{version} ({releaseDate})
                    </span>
                  </div>

                  <h3 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                    Candor IDE for {heroPlatformData.name}
                  </h3>

                  {/* Suggested File Pill */}
                  <div className="flex flex-wrap items-center gap-2 pt-0.5">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-xl bg-slate-100 border border-slate-200/80 text-slate-800 text-xs font-mono">
                      <FileCode className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                      <span className="text-slate-500 font-sans font-medium text-[11px]">File:</span>
                      <span className="font-bold text-blue-700 select-all">{suggestedFileName}</span>
                      <span className="text-slate-400 font-sans text-[11px]">({activeHeroBuild.size})</span>
                    </div>
                    <span className="text-xs text-slate-500 font-medium">
                      {heroPlatformData.tagline}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right: Download CTA Button */}
              <div className="shrink-0 flex flex-col items-stretch sm:items-end gap-1.5">
                <button
                  onClick={() => handleDownloadItem(activeHeroBuild, heroPlatformData.name)}
                  className="px-8 py-4 rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-base shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/35 transition-all hover:-translate-y-0.5 inline-flex items-center justify-center gap-3 group cursor-pointer"
                >
                  <Download className="w-5 h-5 transition-transform group-hover:translate-y-0.5" />
                  <span>Download Now</span>
                </button>
                <span className="text-xs text-slate-400 text-center sm:text-right font-medium">
                  Direct Installer • Instant Download
                </span>
              </div>
            </div>

            {/* Inline Package Format Selector */}
            <div className="mt-6 pt-5 border-t border-slate-100 text-left">
              <div className="flex items-center justify-between text-xs text-slate-500 mb-2.5">
                <span className="font-semibold text-slate-700 flex items-center gap-1.5">
                  <Layers className="w-4 h-4 text-blue-600" />
                  Package Formats for {heroPlatformData.name}:
                </span>
                <span className="text-xs text-slate-400 hidden sm:inline">
                  Click to choose package format
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {heroPlatformData.architectures.map((arch) => {
                  const isSelected = activeHeroBuild.id === arch.id;
                  // Clean up name and badge to prevent repetition of (.exe), (.zip), etc.
                  const cleanName = arch.name.replace(/\s*\(\.[a-z0-9/ ]+\)/i, '');
                  const cleanBadge = arch.badge ? arch.badge.replace(/\s*\(\.[a-z0-9/ ]+\)/i, '') : null;

                  return (
                    <button
                      key={arch.id}
                      type="button"
                      onClick={() => setSelectedHeroBuildId(arch.id)}
                      className={`p-3 rounded-xl text-xs transition-all flex items-center justify-between border cursor-pointer ${
                        isSelected
                          ? 'bg-blue-50/80 border-blue-500 text-blue-900 shadow-xs ring-1 ring-blue-500/30'
                          : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50/70'
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        {isSelected ? (
                          <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />
                        ) : (
                          <span className="w-4 h-4 rounded-full border border-slate-300 shrink-0" />
                        )}
                        <span className={`font-semibold ${isSelected ? 'text-blue-900 font-bold' : 'text-slate-800'}`}>
                          {cleanName}
                        </span>
                        <span className="font-mono text-[11px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200/50">
                          {arch.format}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <span className="text-[11px] text-slate-400 font-medium">
                          {arch.size}
                        </span>
                        {cleanBadge && (
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              isSelected
                                ? 'bg-blue-600 text-white'
                                : 'bg-slate-100 text-slate-600 border border-slate-200'
                            }`}
                          >
                            {cleanBadge}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Quick OS Switcher & Links Row */}
            <div className="mt-5 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2.5 flex-wrap">
                <span className="text-slate-500 font-medium">Need another OS?</span>
                <div className="inline-flex rounded-xl bg-slate-100 p-1 border border-slate-200/60">
                  {[
                    { id: 'windows', label: 'Windows', icon: WindowsIcon },
                    { id: 'macos', label: 'macOS', icon: AppleIcon },
                    { id: 'linux', label: 'Linux', icon: LinuxIcon },
                  ].map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        setSelectedHeroOs(item.id);
                        setSelectedHeroBuildId(null);
                      }}
                      className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 text-xs font-semibold cursor-pointer ${
                        currentHeroOsKey === item.id
                          ? 'bg-white text-slate-900 font-bold shadow-xs'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <item.icon className="w-3.5 h-3.5" />
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <a
                  href="#all-platforms"
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById('all-platforms');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-blue-600 hover:text-blue-800 font-semibold hover:underline inline-flex items-center gap-1"
                >
                  <span>All platforms</span>
                  <span>↓</span>
                </a>
                <span className="text-slate-300">•</span>
                <button
                  type="button"
                  onClick={() => setIsArchiveModalOpen(true)}
                  className="text-blue-600 hover:text-blue-800 font-semibold hover:underline inline-flex items-center gap-1.5 cursor-pointer"
                >
                  <History className="w-3.5 h-3.5 text-blue-600" />
                  <span>Older Versions Popup</span>
                </button>
              </div>
            </div>

            {/* Card Footer: Security Assurance */}
            <div className="mt-5 pt-3.5 border-t border-slate-100 flex items-center justify-center text-xs text-slate-500 bg-slate-50/60 -mx-6 -mb-6 sm:-mx-8 sm:-mb-8 px-6 sm:px-8 py-3 rounded-b-3xl">
              <span className="text-slate-500 text-xs flex items-center gap-1.5 font-medium">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                Community Edition • 100% Virus-Free &amp; Verified Code-Signed
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Main Multi-OS Platform Section ── */}
      <section id="all-platforms" className="py-12 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-center justify-between mb-8 sm:mb-12 gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              All Supported Platforms & Architectures
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Select your operating system and installer package below.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <button
              type="button"
              onClick={() => setIsArchiveModalOpen(true)}
              className="px-3.5 py-1.5 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 text-xs sm:text-sm font-bold flex items-center gap-1.5 transition-all shadow-2xs hover:-translate-y-0.5"
            >
              <History className="w-4 h-4 text-blue-600" />
              <span>Older Versions Popup</span>
            </button>

            {/* Filter Tabs */}
            <div className="flex rounded-xl bg-slate-200/70 p-1 text-xs sm:text-sm font-semibold">
              {[
                { id: 'all', label: 'All Platforms' },
                { id: 'windows', label: 'Windows' },
                { id: 'macos', label: 'macOS' },
                { id: 'linux', label: 'Linux' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 sm:px-4 py-1.5 rounded-lg transition-all ${
                    activeTab === tab.id
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── 3-Column OS Cards Grid ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          {/* 1. Windows Card */}
          {(activeTab === 'all' || activeTab === 'windows') && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl hover:border-blue-300 transition-all duration-200 flex flex-col overflow-hidden">
              <div className="p-6 border-b border-slate-100 bg-gradient-to-b from-blue-50/40 to-transparent">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-md shadow-blue-500/20">
                    <WindowsIcon className="w-6 h-6" />
                  </div>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-100 text-blue-700">
                    v{version}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-slate-900">{platforms.windows.name}</h3>
                <p className="text-xs text-slate-500 mt-1">{platforms.windows.tagline}</p>
              </div>

              <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                <div className="space-y-3">
                  {platforms.windows.architectures.map((item) => (
                    <div
                      key={item.id}
                      className="border border-slate-200 rounded-xl p-3.5 hover:border-blue-400 hover:bg-blue-50/30 transition-all flex items-center justify-between gap-3 group"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-800">{item.name}</span>
                          {item.badge && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                              {item.badge}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2">
                          <span>{item.arch}</span>
                          <span>•</span>
                          <span>{item.format}</span>
                          <span>•</span>
                          <span>{item.size}</span>
                        </div>
                      </div>

                      <button
                        onClick={() => handleDownloadItem(item, platforms.windows.name)}
                        className="px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-600 text-blue-700 hover:text-white font-semibold text-xs transition-colors shrink-0 flex items-center gap-1 border border-blue-200 hover:border-transparent"
                      >
                        <Download className="w-3.5 h-3.5" />
                        Download
                      </button>
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                  <span>Standard & Portable</span>
                  <button
                    onClick={() => setSelectedShaModal(platforms.windows)}
                    className="text-blue-600 hover:underline inline-flex items-center gap-1 font-medium"
                  >
                    View Checksums
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 2. macOS Card */}
          {(activeTab === 'all' || activeTab === 'macos') && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl hover:border-blue-300 transition-all duration-200 flex flex-col overflow-hidden">
              <div className="p-6 border-b border-slate-100 bg-gradient-to-b from-slate-100/60 to-transparent">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center shadow-md shadow-slate-900/20">
                    <AppleIcon className="w-6 h-6" />
                  </div>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">
                    v{version}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-slate-900">{platforms.macos.name}</h3>
                <p className="text-xs text-slate-500 mt-1">{platforms.macos.tagline}</p>
              </div>

              <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                <div className="space-y-3">
                  {platforms.macos.architectures.map((item) => (
                    <div
                      key={item.id}
                      className="border border-slate-200 rounded-xl p-3.5 hover:border-blue-400 hover:bg-blue-50/30 transition-all flex items-center justify-between gap-3 group"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-800">{item.name}</span>
                          {item.badge && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                              {item.badge}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2">
                          <span>{item.arch}</span>
                          <span>•</span>
                          <span>{item.format}</span>
                          <span>•</span>
                          <span>{item.size}</span>
                        </div>
                      </div>

                      <button
                        onClick={() => handleDownloadItem(item, platforms.macos.name)}
                        className="px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-600 text-blue-700 hover:text-white font-semibold text-xs transition-colors shrink-0 flex items-center gap-1 border border-blue-200 hover:border-transparent"
                      >
                        <Download className="w-3.5 h-3.5" />
                        Download
                      </button>
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                  <span>Apple Silicon & Intel</span>
                  <button
                    onClick={() => setSelectedShaModal(platforms.macos)}
                    className="text-blue-600 hover:underline inline-flex items-center gap-1 font-medium"
                  >
                    View Checksums
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 3. Linux Card */}
          {(activeTab === 'all' || activeTab === 'linux') && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl hover:border-blue-300 transition-all duration-200 flex flex-col overflow-hidden">
              <div className="p-6 border-b border-slate-100 bg-gradient-to-b from-amber-50/40 to-transparent">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-600 text-white flex items-center justify-center shadow-md shadow-amber-500/20">
                    <LinuxIcon className="w-6 h-6" />
                  </div>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800">
                    v{version}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-slate-900">{platforms.linux.name}</h3>
                <p className="text-xs text-slate-500 mt-1">{platforms.linux.tagline}</p>
              </div>

              <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                <div className="space-y-3">
                  {platforms.linux.architectures.map((item) => (
                    <div
                      key={item.id}
                      className="border border-slate-200 rounded-xl p-3.5 hover:border-blue-400 hover:bg-blue-50/30 transition-all flex items-center justify-between gap-3 group"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-800">{item.name}</span>
                          {item.badge && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                              {item.badge}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2">
                          <span>{item.arch}</span>
                          <span>•</span>
                          <span>{item.format}</span>
                          <span>•</span>
                          <span>{item.size}</span>
                        </div>
                      </div>

                      <button
                        onClick={() => handleDownloadItem(item, platforms.linux.name)}
                        className="px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-600 text-blue-700 hover:text-white font-semibold text-xs transition-colors shrink-0 flex items-center gap-1 border border-blue-200 hover:border-transparent"
                      >
                        <Download className="w-3.5 h-3.5" />
                        Download
                      </button>
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                  <span>Debian &amp; Ubuntu (.deb)</span>
                  <button
                    onClick={() => setSelectedShaModal(platforms.linux)}
                    className="text-blue-600 hover:underline inline-flex items-center gap-1 font-medium"
                  >
                    View Checksums
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Previous Versions & Release Archive Callout Card ── */}
        <div id="previous-releases" className="mt-16 sm:mt-24 pt-12 border-t border-slate-200">
          <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 rounded-3xl p-6 sm:p-10 text-white shadow-xl relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8 border border-slate-800">
            <div className="space-y-2 text-center md:text-left max-w-xl">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-semibold">
                <History className="w-3.5 h-3.5" />
                <span>Historical Release Archive</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                Previous &amp; Older Versions Archive
              </h3>
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                Need an earlier build for legacy database infrastructure, staging parity, or rollbacks? Open our interactive release archive popup with version changelogs and verified downloads.
              </p>

              <div className="flex flex-wrap items-center gap-2 pt-2 justify-center md:justify-start">
                {previousVersionsList.map((pv) => (
                  <span
                    key={pv.version}
                    className="text-[11px] font-mono px-2.5 py-1 rounded-lg bg-white/10 text-slate-200 border border-white/10"
                  >
                    {pv.tag || `v${pv.version}`} • {pv.releaseDate.split(',')[0]}
                  </span>
                ))}
              </div>
            </div>

            <div className="shrink-0 flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={() => setIsArchiveModalOpen(true)}
                className="px-7 py-3.5 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-sm sm:text-base shadow-lg shadow-blue-600/30 transition-all hover:-translate-y-0.5 inline-flex items-center gap-2.5 cursor-pointer"
              >
                <History className="w-5 h-5" />
                <span>Open Previous Versions Popup</span>
              </button>
              <span className="text-[11px] text-slate-400">
                Opens modal with all versions &amp; scroll bar
              </span>
            </div>
          </div>
        </div>

        {/* ── Enterprise / Preview Access Strip ── */}
        <div className="mt-12 bg-gradient-to-r from-slate-900 to-indigo-950 rounded-2xl p-6 sm:p-8 text-white shadow-xl flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 sm:max-w-xl text-center sm:text-left">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-xs font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              Preview & Nightly Channel
            </div>
            <h3 className="text-xl sm:text-2xl font-bold tracking-tight">
              Looking for Beta Features or Enterprise MSI Deployments?
            </h3>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              Request access to preview versions with experimental database engines, custom air-gapped bundles, or tailored SOC2 compliance packages.
            </p>
          </div>

          <button
            onClick={() => {
              if (onOpenContact) {
                onOpenContact(
                  'Request Access to Preview / Enterprise Build',
                  'I am interested in testing preview builds or enterprise deployment packages for Candor SI IDE.'
                );
              }
            }}
            className="px-6 py-3 rounded-xl bg-white text-slate-900 hover:bg-blue-50 font-bold text-sm shadow-md transition-all shrink-0 hover:-translate-y-0.5"
          >
            Request Access →
          </button>
        </div>
      </section>

      {/* ── Installation Quickstart Guide ── */}
      <section className="py-12 sm:py-16 bg-white border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Quick Installation Guide
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Get Candor SI IDE running on your machine in under 60 seconds.
            </p>
          </div>

          {/* OS Switcher */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex rounded-xl bg-slate-100 p-1 text-sm font-semibold">
              <button
                onClick={() => setActiveInstallTab('windows')}
                className={`px-5 py-2 rounded-lg transition-all flex items-center gap-2 ${
                  activeInstallTab === 'windows' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600'
                }`}
              >
                <WindowsIcon className="w-4 h-4" />
                Windows
              </button>
              <button
                onClick={() => setActiveInstallTab('macos')}
                className={`px-5 py-2 rounded-lg transition-all flex items-center gap-2 ${
                  activeInstallTab === 'macos' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
                }`}
              >
                <AppleIcon className="w-4 h-4" />
                macOS
              </button>
              <button
                onClick={() => setActiveInstallTab('linux')}
                className={`px-5 py-2 rounded-lg transition-all flex items-center gap-2 ${
                  activeInstallTab === 'linux' ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-600'
                }`}
              >
                <LinuxIcon className="w-4 h-4" />
                Linux
              </button>
            </div>
          </div>

          {/* Instructions Box */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 sm:p-8">
            {activeInstallTab === 'windows' && (
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-7 h-7 rounded-full bg-blue-600 text-white font-bold text-xs flex items-center justify-center shrink-0">1</div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Run the Candor Setup Wizard</h4>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Double-click the downloaded <code className="bg-white px-1.5 py-0.5 rounded border border-slate-200 text-blue-700 font-mono">CandorAI-Setup-v{version}.zip</code> or <code className="bg-white px-1.5 py-0.5 rounded border border-slate-200 text-blue-700 font-mono">.exe</code> file.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-7 h-7 rounded-full bg-blue-600 text-white font-bold text-xs flex items-center justify-center shrink-0">2</div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">SmartScreen or Security Prompt</h4>
                    <p className="text-xs text-slate-600 mt-0.5">
                      If Windows SmartScreen prompts on first launch, click <em>&ldquo;More info&rdquo;</em> and then <em>&ldquo;Run anyway&rdquo;</em>.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-7 h-7 rounded-full bg-blue-600 text-white font-bold text-xs flex items-center justify-center shrink-0">3</div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Sign in with Work Email</h4>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Authenticate with your Candor credentials to unlock automatic schema synchronization and local project workspaces.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeInstallTab === 'macos' && (
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-7 h-7 rounded-full bg-slate-900 text-white font-bold text-xs flex items-center justify-center shrink-0">1</div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Open DMG and Drag to Applications</h4>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Mount <code className="bg-white px-1.5 py-0.5 rounded border border-slate-200 text-slate-800 font-mono">CandorAI-mac-arm64-v{version}.dmg</code> and drag Candor SI into your <code className="bg-white px-1.5 py-0.5 rounded border border-slate-200 text-slate-800 font-mono">/Applications</code> folder.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-7 h-7 rounded-full bg-slate-900 text-white font-bold text-xs flex items-center justify-center shrink-0">2</div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">macOS Gatekeeper Verification</h4>
                    <p className="text-xs text-slate-600 mt-0.5">
                      On first launch, if macOS alerts about an unidentified developer, go to <em>System Settings &gt; Privacy &amp; Security</em> and click <em>Open Anyway</em>, or run in Terminal:
                    </p>
                    <div className="mt-2 bg-slate-900 text-slate-100 rounded-lg p-2.5 font-mono text-xs flex items-center justify-between">
                      <code>xattr -cr /Applications/CandorAI.app</code>
                      <button
                        onClick={() => handleCopySha('xattr -cr /Applications/CandorAI.app')}
                        className="text-slate-400 hover:text-white"
                        title="Copy command"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-7 h-7 rounded-full bg-slate-900 text-white font-bold text-xs flex items-center justify-center shrink-0">3</div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Ready to Vibe Code</h4>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Launch via Spotlight (<kbd className="bg-white px-1 py-0.5 rounded border border-slate-300 text-[10px]">Cmd + Space</kbd>) and connect your first database.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeInstallTab === 'linux' && (
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-7 h-7 rounded-full bg-amber-600 text-white font-bold text-xs flex items-center justify-center shrink-0">1</div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Download Debian / Ubuntu (.deb) Package</h4>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Save <code className="bg-white px-1.5 py-0.5 rounded border border-slate-200 text-amber-700 font-mono">candor-ai_{version}_amd64.deb</code> to your system.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-7 h-7 rounded-full bg-amber-600 text-white font-bold text-xs flex items-center justify-center shrink-0">2</div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Install via APT or DPKG</h4>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Open Terminal in your download directory and install the package:
                    </p>
                    <div className="mt-1.5 bg-slate-900 text-slate-100 rounded-lg p-2.5 font-mono text-xs flex items-center justify-between">
                      <code>sudo apt install ./candor-ai_{version}_amd64.deb</code>
                      <button
                        onClick={() => handleCopySha(`sudo apt install ./candor-ai_${version}_amd64.deb`)}
                        className="text-slate-400 hover:text-white"
                        title="Copy command"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-7 h-7 rounded-full bg-amber-600 text-white font-bold text-xs flex items-center justify-center shrink-0">3</div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Launch Candor SI</h4>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Launch Candor SI from your desktop applications menu or run <code className="bg-white px-1.5 py-0.5 rounded border border-slate-200 text-slate-800 font-mono">candor-ai</code> from terminal.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── System Requirements ── */}
      <section className="py-12 sm:py-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            System Requirements
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Recommended hardware specifications for running Candor local AST schema pipelines smoothly.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm text-center">
            <Cpu className="w-7 h-7 text-blue-600 mx-auto mb-2" />
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Processor</div>
            <div className="font-bold text-slate-900 text-sm mt-1">4-Core CPU</div>
            <div className="text-xs text-slate-500 mt-0.5">Apple Silicon or x64 @ 2.0GHz+</div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm text-center">
            <Layers className="w-7 h-7 text-indigo-600 mx-auto mb-2" />
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Memory (RAM)</div>
            <div className="font-bold text-slate-900 text-sm mt-1">8 GB Minimum</div>
            <div className="text-xs text-slate-500 mt-0.5">16 GB recommended for large DBs</div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm text-center">
            <HardDrive className="w-7 h-7 text-emerald-600 mx-auto mb-2" />
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Disk Storage</div>
            <div className="font-bold text-slate-900 text-sm mt-1">1.5 GB SSD</div>
            <div className="text-xs text-slate-500 mt-0.5">For app install and local cache</div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm text-center">
            <Shield className="w-7 h-7 text-amber-600 mx-auto mb-2" />
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">OS Support</div>
            <div className="font-bold text-slate-900 text-sm mt-1">Win 10+, macOS 12+</div>
            <div className="text-xs text-slate-500 mt-0.5">Ubuntu 20.04+ / Debian 11+</div>
          </div>
        </div>
      </section>

      {/* ── FAQ Section ── */}
      <section className="py-12 sm:py-16 bg-slate-100/60 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Frequently Asked Questions
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Common questions regarding downloads, updates, and licensing.
            </p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden transition-all shadow-sm"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? -1 : idx)}
                  className="w-full px-5 py-4 text-left font-bold text-slate-900 flex items-center justify-between gap-4 hover:bg-slate-50/80 transition-colors"
                >
                  <span className="text-sm sm:text-base">{faq.q}</span>
                  <ChevronDown
                    className={`w-4 h-4 text-slate-400 transition-transform ${
                      expandedFaq === idx ? 'transform rotate-180 text-blue-600' : ''
                    }`}
                  />
                </button>
                {expandedFaq === idx && (
                  <div className="px-5 pb-5 pt-1 text-xs sm:text-sm text-slate-600 leading-relaxed border-t border-slate-100">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Older Versions / Release Archive Popup Modal ── */}
      {isArchiveModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsArchiveModalOpen(false);
          }}
        >
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full border border-slate-200 p-6 sm:p-8 relative flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="flex items-start justify-between pb-4 border-b border-slate-100 shrink-0">
              <div className="space-y-1">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold">
                  <History className="w-3.5 h-3.5" />
                  <span>Release Archive</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                  Previous &amp; Older Versions Archive
                </h3>
                <p className="text-xs sm:text-sm text-slate-500">
                  Select and download earlier Candor SI IDE desktop builds for Windows, macOS, or Linux.
                </p>
              </div>

              <button
                onClick={() => setIsArchiveModalOpen(false)}
                className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 flex items-center justify-center text-xl font-bold transition-colors shrink-0 ml-4 cursor-pointer"
                aria-label="Close archive modal"
              >
                &times;
              </button>
            </div>

            {/* Version Filter Tabs Bar */}
            <div className="pt-4 pb-2 flex items-center justify-between gap-2 flex-wrap shrink-0">
              <div className="flex flex-wrap gap-1.5 rounded-xl bg-slate-100 p-1 text-xs font-semibold">
                <button
                  onClick={() => setSelectedArchiveVersion('all')}
                  className={`px-3 py-1.5 rounded-lg transition-all ${
                    selectedArchiveVersion === 'all'
                      ? 'bg-white text-slate-900 shadow-sm font-bold'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  All Versions ({previousVersionsList.length})
                </button>
                {previousVersionsList.map((pv) => (
                  <button
                    key={pv.version}
                    onClick={() => setSelectedArchiveVersion(pv.version)}
                    className={`px-2.5 py-1.5 rounded-lg transition-all ${
                      selectedArchiveVersion === pv.version
                        ? 'bg-white text-blue-700 shadow-sm font-bold'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {pv.tag || `v${pv.version}`}
                  </button>
                ))}
              </div>

              <span className="text-xs text-slate-400 font-medium">
                Scroll through builds below ↓
              </span>
            </div>

            {/* Scrollable Container with Dedicated Scrollbar */}
            <div className="flex-1 overflow-y-auto pr-2 sm:pr-3 space-y-4 my-3 max-h-[55vh] sm:max-h-[60vh] scrollbar-thin scrollbar-thumb-blue-400 scrollbar-track-slate-100">
              {filteredPreviousVersions.map((release) => (
                <div
                  key={release.version}
                  className="bg-slate-50/70 hover:bg-white rounded-2xl border border-slate-200 p-5 shadow-xs hover:shadow-md transition-all flex flex-col lg:flex-row lg:items-center justify-between gap-5"
                >
                  <div className="space-y-2 lg:max-w-lg">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <span className="text-base font-bold text-slate-900 flex items-center gap-1.5">
                        <Tag className="w-4 h-4 text-blue-600" />
                        Candor SI IDE {release.tag || `v${release.version}`}
                      </span>
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600 flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-slate-400" />
                        {release.releaseDate}
                      </span>
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                          release.status === 'Maintenance'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-slate-200/80 text-slate-700'
                        }`}
                      >
                        {release.status || 'Archived'}
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                      {release.highlights}
                    </p>
                  </div>

                  {/* Download Options for this version */}
                  <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 shrink-0">
                    {release.downloads.map((dl) => (
                      <button
                        key={dl.os}
                        onClick={() => handleDownloadItem(dl, `${dl.name} (v${release.version})`)}
                        className="px-3 py-2 rounded-xl border border-slate-200 hover:border-blue-500 bg-white hover:bg-blue-50 text-slate-800 hover:text-blue-700 font-semibold text-xs transition-all flex items-center gap-1.5 group shadow-xs hover:-translate-y-0.5"
                        title={`Download ${dl.name} v${release.version} (${dl.size})`}
                      >
                        {dl.os === 'windows' && <WindowsIcon className="w-4 h-4 text-blue-600" />}
                        {dl.os === 'macos' && <AppleIcon className="w-4 h-4 text-slate-900" />}
                        {dl.os === 'linux' && <LinuxIcon className="w-4 h-4 text-amber-600" />}
                        <span className="capitalize">{dl.os}</span>
                        <span className="text-[10px] font-mono text-slate-400 group-hover:text-blue-600">
                          {dl.format}
                        </span>
                        <Download className="w-3.5 h-3.5 text-slate-400 group-hover:text-blue-600 ml-0.5" />
                      </button>
                    ))}
                    <button
                      onClick={() =>
                        setSelectedShaModal({
                          name: `Candor SI v${release.version}`,
                          architectures: release.downloads.map((d) => ({
                            id: `${release.version}-${d.os}`,
                            name: `${d.name} (v${release.version})`,
                            format: d.format,
                            sha256: d.sha256,
                          })),
                        })
                      }
                      className="p-2 rounded-xl border border-slate-200 bg-white text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                      title="View SHA-256 Checksums"
                      aria-label={`View checksums for v${release.version}`}
                    >
                      <Shield className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Modal Footer */}
            <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 shrink-0">
              <span className="flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-emerald-500" />
                All historical builds are cryptographically verified and signed.
              </span>
              <button
                onClick={() => setIsArchiveModalOpen(false)}
                className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-colors cursor-pointer"
              >
                Close Archive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Checksum Modal ── */}
      {selectedShaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full border border-slate-200 p-6 relative">
            <button
              onClick={() => setSelectedShaModal(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 text-xl font-bold"
              aria-label="Close modal"
            >
              &times;
            </button>
            <h3 className="text-lg font-bold text-slate-900 mb-1">
              {selectedShaModal.name} SHA-256 Checksums
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Verify the cryptographic integrity of your downloaded binary.
            </p>

            <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
              {selectedShaModal.architectures.map((arch) => (
                <div key={arch.id} className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <div className="flex items-center justify-between text-xs font-bold text-slate-800 mb-1">
                    <span>{arch.name} ({arch.format})</span>
                    <button
                      onClick={() => handleCopySha(arch.sha256)}
                      className="text-blue-600 hover:underline flex items-center gap-1 font-semibold"
                    >
                      {copiedSha === arch.sha256 ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                      Copy
                    </button>
                  </div>
                  <div className="font-mono text-[11px] text-slate-600 bg-white p-2 rounded border border-slate-200 break-all select-all">
                    {arch.sha256}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 pt-3 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => setSelectedShaModal(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
